<!-- Disclosure for overriding the data attributes (AADT band plus any extras) that drive the recommendations. -->
<script module lang="ts">
	import { ETrafficVolume, TRAFFIC_VOLUME_LABEL } from '@safesystem/client-playbook-lib';

	// `aadt_band` stores the playbook's TRAFFIC_VOLUME_LABEL text (e.g. "<2,000"), so map it back to the enum.
	export const aadtFromLabel = (label: unknown) =>
		Object.values(ETrafficVolume).find((volume) => TRAFFIC_VOLUME_LABEL[volume] === label);

	const aadtOptions = Object.values(ETrafficVolume).map((volume) => ({
		value: volume,
		label: TRAFFIC_VOLUME_LABEL[volume]
	}));
</script>

<script lang="ts">
	import type { Snippet } from 'svelte';
	import SegmentedControl from '#lib/components/SegmentedControl.svelte';
	import IconChevronUp from '#lib/assets/chevron-up.svg?raw';

	interface Props {
		summary: string;
		aadtBand: ETrafficVolume | undefined;
		children?: Snippet;
	}

	let { summary, aadtBand = $bindable(), children }: Props = $props();
</script>

<details class="dataAttributes">
	<summary>
		<span class="chevron" aria-hidden="true">{@html IconChevronUp}</span>
		{summary}
	</summary>
	<div class="summarised">
		<SegmentedControl
			label="Average Annual Daily Traffic:"
			options={aadtOptions}
			value={aadtBand ?? ''}
			onchange={(value) => (aadtBand = value as ETrafficVolume)} />
		{@render children?.()}
	</div>
</details>

<style>
	.dataAttributes {
		margin-top: var(--space-4);

		summary {
			display: flex;
			align-items: center;
			gap: var(--space-2);
			cursor: pointer;
			padding: var(--space-1) 0;
			font-weight: var(--weight-bold);
			list-style: none;

			&::-webkit-details-marker {
				display: none;
			}
		}

		.summarised {
			display: flex;
			flex-direction: column;
			gap: var(--space-4);
			margin-left: var(--space-1);
			border-left: 2px solid var(--border-color);
			padding: var(--space-1) 0 var(--space-4) var(--space-4);
		}

		.chevron {
			display: inline-flex;
			transition: transform 0.15s ease;
			width: var(--space-4);
			height: var(--space-4);

			:global(svg) {
				width: 100%;
				height: 100%;
			}
		}

		&[open] {
			.chevron {
				transform: rotate(180deg);
			}

			summary {
				margin-bottom: var(--space-1);
			}
		}
	}
</style>
